import React, { useMemo, useState } from "react";

const SlaveStatsTables = ({ detailedMetrics, selectedEvent }) => {
  // Internal state for Transfers per AMKA table
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({ key: 'transferCount', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAndSortedAmkaRows = useMemo(() => {
    let items = [...(detailedMetrics?.transfersPerAmka || [])];

    if (searchTerm) {
      items = items.filter(item =>
        item.donorAmka?.toLowerCase().startsWith(searchTerm.toLowerCase())
      );
    }

    if (sortConfig.key !== null) {
      items.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return items;
  }, [detailedMetrics?.transfersPerAmka, sortConfig, searchTerm]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentAmkaRows = filteredAndSortedAmkaRows.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredAndSortedAmkaRows.length / rowsPerPage);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const requestSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (!detailedMetrics) return null;

  return (
    <div className="mt-5">
      <h3>Λεπτομέρειες για: {selectedEvent?.eventName} ({selectedEvent?.eventCode})</h3>

      <div className="row mt-4">
        <div className="col-md-12">
          <h4>Μεταβιβάσεις για Eligible Events</h4>
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="table-light">
                <tr>
                  <th>Κωδικός Event</th>
                  <th>Όνομα Event</th>
                  <th>Αριθμός Μεταβιβάσεων</th>
                </tr>
              </thead>
              <tbody>
                {detailedMetrics.eligibleEventTransfers?.length > 0 ? (
                  detailedMetrics.eligibleEventTransfers.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.eventCode}</td>
                      <td>{item.eventDescription}</td>
                      <td>{item.transferCount}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center">Δεν βρέθηκαν δεδομένα.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="row mt-4">
        <div className="col-md-12">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h4 className="mb-0">Μεταβιβάσεις ανά ΑΜΚΑ</h4>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th colSpan="2" className="p-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3">
                        <div className="input-group input-group-sm d-inline-flex" style={{ maxWidth: '250px' }}>
                          <span className="input-group-text" id="search-amka">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="bi bi-search" viewBox="0 0 16 16">
                              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                            </svg>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Αναζήτηση AMKA..."
                            aria-label="Αναζήτηση AMKA"
                            aria-describedby="search-amka"
                            value={searchTerm}
                            onChange={handleSearchChange}
                          />
                        </div>
                        {searchTerm && (
                          <span className="small text-muted ms-1" style={{ whiteSpace: 'nowrap', minWidth: '150px' }}>
                            <strong>Αποτελέσματα:</strong> {filteredAndSortedAmkaRows.length}
                          </span>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <label htmlFor="rowsPerPageSelect" className="mb-0 small text-muted">Εγγραφές ανά σελίδα:</label>
                        <select
                          id="rowsPerPageSelect"
                          className="form-select form-select-sm"
                          style={{ width: 'auto' }}
                          value={rowsPerPage}
                          onChange={handleRowsPerPageChange}
                        >
                          {[5, 10, 20, 25, 50, 100].map(val => (
                            <option key={val} value={val}>{val}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th onClick={() => requestSort('donorAmka')} style={{ cursor: 'pointer' }}>
                    AMKA {sortConfig.key === 'donorAmka' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => requestSort('transferCount')} style={{ cursor: 'pointer' }}>
                    Μεταβιβάσεις ανά ΑΜΚΑ {sortConfig.key === 'transferCount' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {detailedMetrics.transfersPerAmka?.length > 0 ? (
                  currentAmkaRows.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.donorAmka}</td>
                      <td>{item.transferCount}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2" className="text-center">Δεν βρέθηκαν δεδομένα.</td>
                  </tr>
                )}
              </tbody>
              {totalPages > 1 && (
                <tfoot className="table-light">
                  <tr>
                    <td colSpan="2">
                      <nav>
                        <ul className="pagination justify-content-center mb-0 mt-2">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => paginate(currentPage - 1)}>Προηγούμενο</button>
                          </li>
                          {[...Array(totalPages)].map((_, i) => {
                            const page = i + 1;
                            if (totalPages > 10) {
                              if (page === 1 || page === totalPages || (page >= currentPage - 2 && page <= currentPage + 2)) {
                                return (
                                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button className="page-link" onClick={() => paginate(page)}>{page}</button>
                                  </li>
                                );
                              } else if (page === currentPage - 3 || page === currentPage + 3) {
                                return <li key={`ellipsis-${page}`} className="page-item disabled"><span className="page-link">...</span></li>;
                              }
                              return null;
                            }
                            return (
                              <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                <button className="page-link" onClick={() => paginate(page)}>{page}</button>
                              </li>
                            );
                          })}
                          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button className="page-link" onClick={() => paginate(currentPage + 1)}>Επόμενο</button>
                          </li>
                        </ul>
                        <div className="text-center mt-1 small text-muted">
                          Σελίδα {currentPage} από {totalPages}
                        </div>
                      </nav>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlaveStatsTables;
